import type { EditTarget } from '@web-studio/shared-schemas';
import { EditableContent } from '../../edit/EditableContent';
import type { ParsedFloorTable } from '../../lib/parseFloorTables';
import type { ParsedFormField } from '../../lib/parseFormFields';
import { pickChrome } from '../../lib/siteChromeCopy';

function autocompleteFor(name: string): string | undefined {
  if (name === 'email') return 'email';
  if (name === 'name') return 'name';
  if (name === 'phone') return 'tel';
  return undefined;
}

function isWideField(field: ParsedFormField): boolean {
  return field.type === 'textarea' || field.name === 'message' || field.name === 'allergens';
}

export function FormFieldControl({
  field,
  locale,
  fieldClassName = 'ws-form__field',
  editable,
  activeEditTarget,
}: {
  field: ParsedFormField;
  locale: string;
  fieldClassName?: string;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
}) {
  const autoComplete = autocompleteFor(field.name);
  const wide = isWideField(field);
  const labelFieldKey = `formFieldLabel:${field.name}`;

  return (
    <label
      className={[fieldClassName, wide ? 'ws-form__field--wide' : ''].filter(Boolean).join(' ')}
    >
      <span>
        <EditableContent
          fieldKey={labelFieldKey}
          editable={editable}
          activeEditTarget={activeEditTarget}
        >
          {field.label}
        </EditableContent>
        {field.required ? (
          <abbr className="ws-form__required" title={pickChrome('requiredMark', locale)}>
            *
          </abbr>
        ) : null}
      </span>
      {field.type === 'textarea' ? (
        <textarea name={field.name} rows={4} required={field.required || undefined} />
      ) : (
        <input
          type={field.type === 'tel' ? 'tel' : field.type}
          name={field.name}
          required={field.required || undefined}
          autoComplete={autoComplete}
          min={field.name === 'guests' ? 1 : undefined}
          max={field.name === 'guests' ? 20 : undefined}
          defaultValue={field.name === 'guests' ? 2 : undefined}
        />
      )}
    </label>
  );
}

export function FloorTableSelect({
  tables,
  showCapacity,
  locale,
  label,
  fieldClassName = 'ws-form__field',
  editable,
  activeEditTarget,
}: {
  tables: ParsedFloorTable[];
  showCapacity: boolean;
  locale: string;
  /** Override for chrome default (`fieldTable`). */
  label?: string;
  fieldClassName?: string;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
}) {
  if (tables.length === 0) {
    return null;
  }

  const displayLabel = label?.trim() || pickChrome('fieldTable', locale);

  return (
    <label className={`${fieldClassName} ws-form__field--wide`}>
      <span>
        <EditableContent
          fieldKey="tableLabel"
          editable={editable}
          activeEditTarget={activeEditTarget}
        >
          {displayLabel}
        </EditableContent>
        <abbr className="ws-form__required" title={pickChrome('requiredMark', locale)}>
          *
        </abbr>
      </span>
      <select name="tableId" required>
        {tables.map((table) => {
          const zone = table.zone ? ` · ${table.zone}` : '';
          const capacity = showCapacity ? ` (${table.capacity})` : '';
          return (
            <option key={table.id} value={table.id}>
              {`${table.label}${zone}${capacity}`}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export function ConsentCheckbox({
  label,
  className = 'ws-form__consent',
}: {
  label: string;
  className?: string;
}) {
  if (!label) {
    return null;
  }
  return (
    <label className={className}>
      <input type="checkbox" name="consent" required />
      <span>{label}</span>
    </label>
  );
}
